<template lang="html">
  <div :key="$store.getters['resource/currentResource'].key">
    <loader v-if="loading" />
    <help :title="$t('help.available_variables')">
      <template v-slot:text>
        <b-container class="pt-3">
          <b-col v-for="(list, key) in availableVariablesFormatted" :key="key">
            <h3>{{ key }}</h3>
            <b-row>
              <b-col 
                v-for="variable in list" 
                :key="variable" 
                style="flex: 0 1 480px; margin: 5px"
              >
                {{ variable }}
              </b-col>
            </b-row>
            <hr>
          </b-col>
        </b-container>
      </template>
    </help>
    <b-row>
      <b-col :md="columnWith" v-for="(input, key) in fields" :key="key">
        <label :for="input.name">
          {{ input.label }}
          <span class="text-danger">{{ $t("form.required_star") }}</span>
        </label>
        <div class="mb-4">
          <b-form-input 
            v-model="params[input.name]" 
            :placeholder="input.placeholder" 
            :name="input.name"
            :id="input.name" 
            @input="(value) => createParamsObject(input.name, value)"
          />
          <span 
            class="text-danger position-absolute error" 
            v-if="!$v[input.name].required && $v[input.name].$dirty"
          >
            {{ $t("form.required_field") }}
          </span>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-row>
          <b-col xl="6">
            <span class="mb-2 d-block">
              {{ $t("form.html") }}
              <span class="text-danger">{{ $t("form.required_star") }}</span>
            </span>
            <b-card body-class="p-0">
              <editor 
                v-model="params.html" 
                lang="html" 
                theme="chrome" 
                width="100%" 
                height="600" 
                @init="editorInit"
                @input="
                  (value) => {
                    params.html = value;
                    createParamsObject('html', value);
                  }
                "
              />
            </b-card>
            <span 
              class="text-danger position-absolute error" 
              v-if="!$v.html.required && $v.html.$dirty"
            >
              {{ $t("form.required_field") }}
            </span>
          </b-col>
          <b-col xl="6">
            <span class="mb-2 d-block">{{ $t("form.preview") }}</span>
            <b-card body-class="p-0">
              <iframe :srcDoc="params.html" style="height: 600px" class="w-100" />
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <form-buttons @submit="debounceSubmit" v-if="showButtons" />
  </div>
</template>

<script>
import { debounce, isEmpty } from 'lodash';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'PdfView',
  middleware: 'auth',
  props: {
    fields: {
      type: Array,
      require: true,
      default: Array,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    columnWith: {
      type: Number,
      default: 4,
    },
    userPrefillId: {
      type: String,
      default: '',
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      prefillParams: {},
      loading: false,
      title: '',
      html: '',
      availableVariables: {},
      params: {
        title: '',
        html: '',
      },
      paramsMutated: {},
      debounceSubmit: () => { },
    };
  },
  computed: {
    availableVariablesFormatted() {
      let propertiesArray = {
        Global: [],
      };

      for (const key in this.availableVariables) {
        typeof this.availableVariables[key] === 'object'
          ? (propertiesArray[key] = this.propertiesToArray({
            [key]: this.availableVariables[key],
          }).sort())
          : propertiesArray.Global.push(key);
      }

      for (const key in propertiesArray) {
        propertiesArray[key] = propertiesArray[key].map((el) => `{{${el}}}`);
      }

      return propertiesArray;
    },
  },
  validations: {
    title: {
      required,
    },
    html: {
      required,
    },
  },
  created() {
    window.addEventListener('keydown', this.saveWithHotkey);
  },
  destroyed() {
    window.removeEventListener('keydown', this.saveWithHotkey);
  },
  mounted() {
    if (this.editing) {
      this.getData(
        `transactions/receipt/templates/default/first-or-create`,
        null,
        true
      ).then(({ data }) => {
        this.prefillParams = data;
        this.setPrefillData(this.prefillParams);
      });
      this.getData('transactions/receipt/templates/variables/available', null, true).then(
        ({ data }) => {
          this.availableVariables = data.items;
        }
      );
    }
    this.debounceSubmit = debounce(this.submit, 1000);
  },
  methods: {
    propertiesToArray(obj) {
      const isObject = (val) => val && typeof val === 'object' && !Array.isArray(val);
      const addDelimiter = (a, b) => (a ? `${a}.${b}` : b);
      const paths = (obj = {}, head = '') => {
        return Object.entries(obj).reduce((product, [key, value]) => {
          let fullPath = addDelimiter(head, key);
          return isObject(value)
            ? product.concat(paths(value, fullPath))
            : product.concat(fullPath);
        }, []);
      };
      return paths(obj);
    },
    saveWithHotkey(e) {
      if (
        (window.navigator.platform.match('Mac') ? e.metaKey : e.ctrlKey) &&
        (e.key === 's' || e.key === 'S')
      ) {
        e.preventDefault();
        this.debounceSubmit();
      }
    },
    editorInit() {
      require('brace/mode/html');
      require('brace/theme/chrome');
    },
    createParamsObject(name, value) {
      this.paramsMutated[name] = value;
    },
    setPrefillData(prefill) {
      this.params.title = prefill.title;
      this.params.html = prefill.html;
    },
    submit() {
      this.title = this.params.title;
      this.subject = this.params.subject;
      this.html = this.params.html;

      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      if (!isEmpty(this.prefillParams)) {
        this.putData(
          `transactions/receipt/templates/${this.prefillParams.id}`,
          this.paramsMutated
        );
      } else {
        this.postData('transactions/receipt/templates', this.paramsMutated);
      }
    },
  },
};
</script>
